/*
 * Helpers for moving a saved project between the browser and the account.
 *
 * An .sb3 project is a zip archive, so its bytes are not valid UTF-8 text.
 * Reading the archive with Blob.text() (or sending it as a JSON string) replaced
 * every invalid byte sequence with U+FFFD, which silently stored a corrupt
 * archive: saving appeared to work, and reopening it produced an empty canvas.
 *
 * Base64 avoids the problem entirely. It is plain ASCII, so it survives JSON
 * and file storage byte for byte, and decoding it reproduces the exact archive
 * the VM produced.
 */
const PROJECT_ENCODING = 'base64';

// The API accepts any base64 alphabet character, padded to a multiple of four.
const BASE64_PATTERN = /^[A-Za-z0-9+/]*={0,2}$/;

/*
 * Turns an ArrayBuffer into base64.
 *
 * Chunked on purpose: String.fromCharCode.apply() with a whole multi-megabyte
 * project would blow the argument limit and throw a RangeError.
 */
const arrayBufferToBase64 = buffer => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        binary += String.fromCharCode.apply(
            null,
            bytes.subarray(offset, offset + chunkSize)
        );
    }
    return btoa(binary);
};

/* Turns base64 back into the bytes the VM can load. */
const base64ToUint8Array = value => {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
};

/*
 * True when a stored payload can actually be turned back into an archive.
 *
 * Projects saved by the earlier text-based code are not base64 and their bytes
 * are already lost, so they are reported as unreadable instead of being handed
 * to the VM as a broken archive (which is what produced the blank canvas).
 */
const isDecodableProject = (content, encoding) => {
    if (typeof content !== 'string' || content.length === 0) return false;
    if (encoding && encoding !== PROJECT_ENCODING) return false;
    if (content.length % 4 !== 0) return false;
    if (!BASE64_PATTERN.test(content)) return false;
    // A base64 payload always starts with the zip local file header, "PK".
    return content.slice(0, 2) === 'UE';
};

export {
    PROJECT_ENCODING,
    arrayBufferToBase64,
    base64ToUint8Array,
    isDecodableProject
};
